import { Loader2 } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import TailwindBottomButtonView from "../../../../../../components/mainBottomButtonView/TailwindBottomButtonView";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../../../../components/ui/common/sheet";
import { TwScope } from "../../../../../../components/ui/TwScope";
import CommonSplitContainer from "../../../../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import ExternalStorageSectionCollapsible from "../components/ExternalStorageSectionCollapsible";
import { ExternalStorageFacilityViewModalProps } from "./externalStorageFacility.types";
import FacilityViewInfoSection from "./components/FacilityViewInfoSection";
import FacilityViewOriginalPanel from "./components/FacilityViewOriginalPanel";
import FacilityViewSourceTable from "./components/FacilityViewSourceTable";
import { useExternalStorageFacilityView } from "./hooks/useExternalStorageFacilityView";

const ENERGY_COLUMNS = [
  { key: "source", header: "Source" },
  { key: "kwh", header: "kWh consumed" },
  { key: "distribution", header: "Distribution" },
  { key: "price", header: "Price per kWh" },
  { key: "total", header: "Total cost" }
];

const HEAT_COLUMNS = [
  { key: "source", header: "Source" },
  { key: "volume", header: "Volume" },
  { key: "price", header: "Price per unit" },
  { key: "total", header: "Total price" }
];

const WATER_COLUMNS = [
  { key: "source", header: "Source" },
  { key: "volume", header: "Volume" },
  { key: "price", header: "Price per unit" },
  { key: "total", header: "Total price" }
];

const REFRIGERANT_COLUMNS = [
  { key: "source", header: "Source" },
  { key: "volume", header: "Volume" },
  { key: "price", header: "Price per unit" },
  { key: "total", header: "Total price" }
];

const ExternalStorageFacilityViewModal = ({
  isOpen,
  onClose,
  facilityId,
  variant = "mapping"
}: ExternalStorageFacilityViewModalProps) => {
  const [infoExpanded, setInfoExpanded] = useState(true);
  const [energyExpanded, setEnergyExpanded] = useState(true);
  const [heatExpanded, setHeatExpanded] = useState(true);
  const [waterExpanded, setWaterExpanded] = useState(true);
  const [refrigerantExpanded, setRefrigerantExpanded] = useState(true);

  const { viewModel, isLoading } = useExternalStorageFacilityView({
    facilityId,
    variant,
    isOpen
  });

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <Sheet open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <SheetContent
        side="right"
        overlayClassName="tw-z-[1200]"
        className="tw-w-full sm:!tw-max-w-4xl !tw-p-0 tw-flex tw-flex-col tw-gap-0 tw-z-[1200]"
      >
        <TwScope className="tw-flex tw-flex-col tw-h-full tw-min-h-0">
          <SheetHeader className="tw-px-5 tw-p-4 tw-border-b tw-border-border tw-shrink-0 tw-items-start">
            <div className="tw-flex tw-flex-col tw-gap-0.5 tw-px-1">
              <SheetTitle className="tw-font-display tw-text-xl">
                {viewModel?.title || "Facility"}
              </SheetTitle>
              {viewModel?.subtitle && (
                <p className="tw-text-sm tw-text-muted-foreground">{viewModel.subtitle}</p>
              )}
            </div>
          </SheetHeader>

          <div className="tw-flex tw-flex-1 tw-min-h-0 tw-flex-col tw-bg-white tw-px-4 tw-pt-4">
            {isLoading ? (
              <div
                className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-py-12"
                aria-busy="true"
                aria-live="polite"
              >
                <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-muted-foreground" />
              </div>
            ) : (
              <CommonSplitContainer
                topChild={
                  <div className="tw-flex tw-flex-col tw-gap-5 tw-pr-1">
                    {viewModel?.showOriginalPanel && (
                      <FacilityViewOriginalPanel rows={viewModel.originalInfoRows} />
                    )}

                    <ExternalStorageSectionCollapsible
                      title="Facility info"
                      expanded={infoExpanded}
                      onToggle={() => setInfoExpanded(prev => !prev)}
                    >
                      <FacilityViewInfoSection rows={viewModel?.infoRows ?? []} />
                    </ExternalStorageSectionCollapsible>

                    <ExternalStorageSectionCollapsible
                      title="Power sources"
                      expanded={energyExpanded}
                      onToggle={() => setEnergyExpanded(prev => !prev)}
                    >
                      <FacilityViewSourceTable
                        title=""
                        columns={ENERGY_COLUMNS}
                        rows={viewModel?.energyRows ?? []}
                      />
                      {!viewModel?.energyRows?.length && (
                        <p className="tw-text-sm tw-text-muted-foreground">No power source data.</p>
                      )}
                    </ExternalStorageSectionCollapsible>

                    <ExternalStorageSectionCollapsible
                      title="Heat sources"
                      expanded={heatExpanded}
                      onToggle={() => setHeatExpanded(prev => !prev)}
                    >
                      <FacilityViewSourceTable
                        title=""
                        columns={HEAT_COLUMNS}
                        rows={viewModel?.heatRows ?? []}
                      />
                      {!viewModel?.heatRows?.length && (
                        <p className="tw-text-sm tw-text-muted-foreground">No heat source data.</p>
                      )}
                    </ExternalStorageSectionCollapsible>

                    <ExternalStorageSectionCollapsible
                      title="Water sources"
                      expanded={waterExpanded}
                      onToggle={() => setWaterExpanded(prev => !prev)}
                    >
                      <FacilityViewSourceTable
                        title=""
                        columns={WATER_COLUMNS}
                        rows={viewModel?.waterRows ?? []}
                      />
                      {!viewModel?.waterRows?.length && (
                        <p className="tw-text-sm tw-text-muted-foreground">No water source data.</p>
                      )}
                    </ExternalStorageSectionCollapsible>

                    <ExternalStorageSectionCollapsible
                      title="Refrigerant sources"
                      expanded={refrigerantExpanded}
                      onToggle={() => setRefrigerantExpanded(prev => !prev)}
                    >
                      <FacilityViewSourceTable
                        title=""
                        columns={REFRIGERANT_COLUMNS}
                        rows={viewModel?.refrigerantRows ?? []}
                      />
                      {!viewModel?.refrigerantRows?.length && (
                        <p className="tw-text-sm tw-text-muted-foreground">
                          No refrigerant source data.
                        </p>
                      )}
                    </ExternalStorageSectionCollapsible>
                  </div>
                }
                bottomChild={
                  <TailwindBottomButtonView
                    cancelButtonStatus={true}
                    cancelButton={onClose}
                    cancelButtonTitle="Close"
                    customHeight={72}
                    customBottomContentMain="!tw-px-2 tw-py-3"
                    testId="external-storage-facility-view-bottom-button"
                  />
                }
                bottomClassName="tw-border-t tw-border-border tw-bg-background"
              />
            )}
          </div>
        </TwScope>
      </SheetContent>
    </Sheet>,
    document.body
  );
};

export default ExternalStorageFacilityViewModal;
